/**
 * Debug logger with ring buffer for in-app debug overlay
 * Enables debugging Android camera issues without Chrome DevTools
 */

export interface DebugLogEntry {
  timestamp: number;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  data?: any;
}

class DebugLogger {
  private logs: DebugLogEntry[] = [];
  private maxLogs = 50; // Keep last 50 logs
  private isEnabled = false;
  private cameraCaptureData: any = null;
  private normalizedPhotoData: any = null;
  private uploadResponse: any = null;

  constructor() {
    // Enable if ?debug=1 in URL
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      this.isEnabled = params.get('debug') === '1' || localStorage.getItem('debugMode') === 'true';
    }
  }

  /**
   * Enable/disable debug mode
   */
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    if (typeof window !== 'undefined') {
      if (enabled) {
        localStorage.setItem('debugMode', 'true');
      } else {
        localStorage.removeItem('debugMode');
      }
    }
  }

  /**
   * Check if debug mode is enabled
   */
  getEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Add log entry
   */
  private addLog(level: DebugLogEntry['level'], message: string, data?: any) {
    const entry: DebugLogEntry = {
      timestamp: Date.now(),
      level,
      message,
      data
    };

    this.logs.push(entry);

    // Keep only last maxLogs entries (ring buffer)
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Also log to console
    const consoleMethod = console[level] || console.log;
    if (data) {
      consoleMethod(`[DEBUG] ${message}`, data);
    } else {
      consoleMethod(`[DEBUG] ${message}`);
    }
  }

  log(message: string, data?: any) {
    this.addLog('log', message, data);
  }

  info(message: string, data?: any) {
    this.addLog('info', message, data);
  }

  warn(message: string, data?: any) {
    this.addLog('warn', message, data);
  }

  error(message: string, data?: any) {
    this.addLog('error', message, data);
  }

  /**
   * Get all logs
   */
  getLogs(): DebugLogEntry[] {
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Camera-specific logging
   */
  setCameraCapture(data: any) {
    this.cameraCaptureData = data;
    this.log('📷 Camera capture completed', data);
  }

  getCameraCapture() {
    return this.cameraCaptureData;
  }

  setNormalizedPhoto(data: any) {
    this.normalizedPhotoData = data;
    this.log('🖼️ Photo normalized', data);
  }

  getNormalizedPhoto() {
    return this.normalizedPhotoData;
  }

  setUploadResponse(data: any) {
    this.uploadResponse = data;
    this.log('☁️ Upload response received', data);
  }

  getUploadResponse() {
    return this.uploadResponse;
  }

  /**
   * Get debug summary for overlay
   */
  getDebugSummary() {
    return {
      logs: this.getLogs().slice(-30), // Last 30 logs for display
      cameraCapture: this.cameraCaptureData,
      normalizedPhoto: this.normalizedPhotoData,
      uploadResponse: this.uploadResponse
    };
  }
}

// Singleton instance
export const debugLogger = new DebugLogger();

// Export convenience functions
export const debugLog = (message: string, data?: any) => debugLogger.log(message, data);
export const debugInfo = (message: string, data?: any) => debugLogger.info(message, data);
export const debugWarn = (message: string, data?: any) => debugLogger.warn(message, data);
export const debugError = (message: string, data?: any) => debugLogger.error(message, data);
